import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  IconButton,
  Typography,
  Box,
  TextField,
  Slide,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  formatCitationName,
  formatCourtName,
  formatJudgeName,
  getContentAfterHeadings,
  wait,
} from "../utils/HelperFunction/textFormatting";
import { toast } from "react-toastify";
import CaseSearchDialogHeader from "../components/UI/CaseSearchDialogHeader";
import { printContent } from "../utils/HelperFunction/actions";
import SearchInputField from "../components/UI/SearchInputField";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CaseLawPreviewDialog = ({
  open,
  onClose,
  title,
  court,
  citation,
  judge,
  caseDescriptionContent,
  caseDescription,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [caseDetails, setCaseDetails] = useState(null);
  const [totalMatches, setTotalMatches] = React.useState(0);
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [highlightedHtml, setHighlightedHtml] = React.useState("");
  const [isCopied, setIsCopied] = useState(false);
  const caseDetail = useSelector((state) => state.caseDetail);
  const { loading, caseItem } = caseDetail;

  const caseDetailsFromCosmos = useSelector(
    (state) => state.caseDetailsFromCosmos
  );
  const { caseItem: additionalCaseDetails } = caseDetailsFromCosmos;

  const formatCaseTitle = (title) => {
    if (!title) return;
    const splitTitle = title.split("Honorable")[0];
    const titleCaseTitle = splitTitle
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    return titleCaseTitle;
  };

  const handleClosePreview = () => {
    dispatch({ type: "CASE_DETAILS_FROM_COSMOS_RESET" });
    setSearchTerm("");
    setCurrentMatch(0);
    setTotalMatches(0);
    onClose();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const highlightText = (html, search) => {
    if (!search) return { html, matchCount: 0 };

    const escapedTerm = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");
    let matchCount = 0;

    const highlightNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const highlightedText = node.textContent.replace(regex, (match) => {
          matchCount++;
          return `<span class="highlight" data-index="${matchCount}">${match}</span>`;
        });
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = highlightedText;
        return tempDiv.innerHTML;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes)
          .map(highlightNode)
          .join("");
        const element = document.createElement(node.nodeName.toLowerCase());
        Array.from(node.attributes).forEach((attr) => {
          element.setAttribute(attr.name, attr.value);
        });
        element.innerHTML = children;
        return element.outerHTML;
      }

      return "";
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const highlightedHtml = highlightNode(doc.body);

    return { html: highlightedHtml, matchCount };
  };

  const handlePrint = () => {
    const markdownContent = highlightedHtml;
    printContent({
      content: markdownContent,
      cssString: "",
      disclaimer: "",
    });
  };

  const handleCopy = async () => {
    setIsCopied(true);
    try {
      await navigator.clipboard.writeText(caseDescription);
      await wait();
      setIsCopied(false);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Case Preview", text: caseDescription });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      toast.error("Share is not supported in your browser.", {
        position: "top-right",
      });
    }
  };

  React.useEffect(() => {
    const result = highlightText(
      getContentAfterHeadings(
        caseDetails?.Case_Description_HTML || caseDescriptionContent
      ),
      searchTerm
    );

    const styledHtml = `
    <style>
      .MsoNormal {
        font-family: 'Roboto', Helvetica, Arial, sans-serif !important;
        line-height: 1.3;
        font-size: 13px
      }

      .MsoNormal b {
        font-size: 13px;
        color: #303f9f;
      }

      p {
        margin-bottom: 16px;
        color: #334155;
      }

      a {
      display: none
      }

      u {
      font-style: normal;
      }

      strong {
       font-style: normal;
      }

      img {
        display:none
      }

      .highlight {
        background-color: yellow;
        font-weight: bold;
      }

      @media (max-width: 768px) {
        .MsoNormal {
          font-size: 12px;
        }

        .MsoNormal b {
          font-size: 12px;
        }
      }

      @media (max-width: 480px) {
        .MsoNormal {
          font-size: 12px;
        }

        .MsoNormal b {
          font-size: 12px;
        }
  }


    </style>
    ${result.html}
  `;

    setHighlightedHtml(styledHtml);
    setTotalMatches(result.matchCount);
    setCurrentMatch(result.matchCount > 0 ? 1 : 0);
  }, [searchTerm, caseDetails, caseDescriptionContent]);

  const caseMetaData = [
    {
      label: "Citation",
      value: formatCitationName(caseItem?.value?.[0]?.Citation_Name || citation),
    },
    {
      label: "Court",
      value: formatCourtName(caseItem?.value?.[0]?.Court || court),
    },
    {
      label: "Judge",
      value: formatJudgeName(caseItem?.value?.[0]?.Judge_Name || judge),
    },
    {
      label: "Appellant",
      value:
        formatCaseTitle(caseItem?.value?.[0]?.Title)?.split("Versus")[0] ||
        title?.split("Versus")[0],
    },
    {
      label: "Respondents",
      value:
        formatCaseTitle(caseItem?.value?.[0]?.Title)?.split("Versus")[1] ||
        title?.split("Versus")[1],
    },
  ];

  const filteredMetaData = caseMetaData.filter((detail) => detail.value);

  useEffect(() => {
    if (caseItem && caseItem?.value?.length > 0) {
      setCaseDetails(caseItem?.value[0]);
    } else {
      setCaseDetails(null);
    }
  }, [caseItem]);

  React.useEffect(() => {
    if (currentMatch > 0) {
      const element = document.querySelector(
        `.highlight[data-index="${currentMatch}"]`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.style.border = "1px solid #303f9f";
      }
    }
  }, [currentMatch]);

  return (
    <Dialog
      open={open}
      onClose={handleClosePreview}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "24px" },
          boxShadow: "0 8px 32px 0 rgba(60, 80, 177, 0.13)",
          background: "linear-gradient(135deg, #fff 80%, #f8fafc 100%)",
        },
      }}
      BackdropProps={{
        sx: {
          background: "rgba(30,41,59,0.25)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <CaseSearchDialogHeader
        component="preview"
        onClose={onClose}
        isCopied={isCopied}
        onCopy={handleCopy}
        onShare={handleShare}
        onPrint={handlePrint}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowY: "auto",
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          boxShadow: {
            xs: "none",
            sm: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          },
          background: "#ffffff",
        }}
      >
        {loading && <Typography textAlign="center">Loading...</Typography>}
        {(caseDetails || title) && (
          <>
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                background: "#ffffff",
              }}
            >
              <SearchInputField
                searchTerm={searchTerm}
                onSearch={handleSearchChange}
                currentMatch={currentMatch}
                setCurrentMatch={setCurrentMatch}
                totalMatches={totalMatches}
              />
            </Box>

            <Typography
              variant="h1"
              sx={{
                fontWeight: 600,
                color: "#1e3a8a",
                fontSize: { xs: "1rem", sm: "1.125rem" },
                my: 1.5,
                lineHeight: 1.3,
              }}
              dangerouslySetInnerHTML={{
                __html:
                  formatCaseTitle(
                    caseDetails?.Title || additionalCaseDetails?.title
                  ) || title,
              }}
            />
            <Box
              sx={{
                height: "100%",
                paddingRight: "8px",
                overflowY: "auto",
                "::-webkit-scrollbar": {
                  width: "5px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#fff",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              {filteredMetaData.map((detail, index) => (
                <Box
                  key={index}
                  sx={{
                    paddingLeft: "5px",
                    marginBottom: "2px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "12px", md: "13px" },
                      color: "#334155",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>{detail.label}:</span>{" "}
                    <span>{detail.value}</span>
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  marginTop: "12px",
                  wordBreak: "break-word",
                  fontFamily: "'Roboto', Helvetica, Arial, sans-serif",
                  fontSize: "13px",
                  lineHeight: 1.3,
                }}
                dangerouslySetInnerHTML={{
                  __html: highlightedHtml,
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default CaseLawPreviewDialog;
